import { Container, SxProps, Typography, useMediaQuery, useTheme } from "@mui/material";
import { FC, ReactNode, useRef } from 'react';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getNavIcon, matchPathInMap } from "../../utils/getNavIcon";
import { BreadcrumbsComponent } from "../AppBarComp/Breadcrumbs";
import { useGlobal } from "../GlobalProviderLayout";
import { CLOSE_TOAST } from "../GlobalProviderLayout/GlobalProviderLayoutModel";
import { breadcrumbsSubdestinationLabelMap, breadcrumbsSubdestinationMap, subdestinationMap } from "../RootLayout/RootLayoutModels";
import { COMPLEX_PATHNAME_TITLES, MOBILE_PATHNAME_TITLES, PATHNAME_TITLES } from "./MainContainerCompModels";
type ContainerProps = {
  children: ReactNode;
  sx?: SxProps
  hasGreyBg?: boolean;
  dataAutomation?: string;
  title?: string;
  hasFullSreenTabletSize?: boolean
};

export const MainContainer: FC<ContainerProps> = ({
  children, sx = {}, hasGreyBg = true, dataAutomation, title, hasFullSreenTabletSize = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const { dispatch } = useGlobal();
  const params = useParams();
  const previousLocation = useRef<string | undefined>(undefined);
  const pathname = location.pathname;
  const navIcon = getNavIcon(location);
  const hasBreadCrumb = navIcon === "breadcrumbs";
  const breadcrumbsSubdestination = matchPathInMap(breadcrumbsSubdestinationMap, location.pathname);
  // eslint-disable-next-line max-len
  const navIconLabel = breadcrumbsSubdestinationLabelMap[breadcrumbsSubdestinationMap[breadcrumbsSubdestination as string]];
  const titles = isMobile ? MOBILE_PATHNAME_TITLES : PATHNAME_TITLES;
  const pathArr = pathname?.split('/') ?? [];
  let pathName = pathArr[1] ? pathArr[1] as keyof typeof titles : "welcome" as keyof typeof titles;
  if (COMPLEX_PATHNAME_TITLES.includes(pathArr[1])) {
    pathName = `${pathArr[1]}-${pathArr[2]}` as keyof typeof titles;
  }
  const hasTitle = Object.keys(titles).find((key) => pathName.includes(key)) && titles[pathName];

  const handleNavIcon = (level?: number) => {
    dispatch({ type: CLOSE_TOAST });
    if (previousLocation.current !== undefined && level) {
      navigate(-level);
    } else if (level) {
      let to = location.pathname;
      for (level; level > 0; level--) {
        const subdest = matchPathInMap(subdestinationMap, to);
        if (subdest) to = subdestinationMap[subdest];
      }
      /**
       * Need to change the behaviour of this for levels > 2
       * This impl. can only retrieve params from the current url,
       * so context of params from historic dest. routes are lost
       * (assuming param names differ from the sub dest.)
       */
      const parameters = to.match(/:[A-Za-z]+/g) || [];
      for (let p of parameters) {
        // replace URL params w/ actual values
        to = to.replace(p, params[p.replace(':', '')] ?? '');
      }
      navigate(to, { replace: true });
    }
  }

  return (
    <Container
      disableGutters
      data-automation={dataAutomation ?? 'mainContainer'}
      className={`${hasGreyBg ? 'greyBg' : ''} ${hasFullSreenTabletSize ? 'tablet-full-screen' : ''} page-container`}
      sx={sx}
    >
      {((hasTitle && !hasBreadCrumb) || (title && !hasBreadCrumb)) && (
        <Typography
          data-automation="navigation-bar-title"
          className="main-title"
        >
          {titles[pathName] || title}
        </Typography>
      )}
      {
        hasBreadCrumb && <BreadcrumbsComponent
          handleNavIcon={handleNavIcon}
          navIconLabel={navIconLabel}
        />
      }
      {children}
    </Container>
  )
}